import { createApp, type App } from 'vue';
import ElementPlus from 'element-plus';
import { ElMessageBox, ElNotification } from 'element-plus';
import SubscriptionSuccessDialog from '@/components/SubscriptionSuccessDialog.vue';
import { i18n } from '@/i18n';

/**
 * 订阅弹窗管理器
 * 用于在任何地方显示订阅相关弹窗（成功、错误等）
 */
class SubscriptionDialogManager {
  private app: App | null = null;
  private container: HTMLElement | null = null;

  /**
   * 显示订阅成功弹窗
   */
  async showSuccessDialog(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // 创建容器元素
        this.container = document.createElement('div');
        document.body.appendChild(this.container);

        // 创建 Vue 应用实例
        this.app = createApp(SubscriptionSuccessDialog, {
          modelValue: true,
          onContinue: () => {
            this.cleanup();
            resolve();
          },
          'onUpdate:modelValue': (visible: boolean) => {
            if (!visible) {
              this.cleanup();
              resolve();
            }
          }
        });

        // 使用 Element Plus 和 i18n
        this.app.use(ElementPlus);
        this.app.use(i18n);

        // 挂载到容器
        this.app.mount(this.container);
      } catch (error) {
        console.error('Failed to mount SubscriptionSuccessDialog:', error);
        this.cleanup();
        reject(error);
      }
    });
  }

  /**
   * 显示错误对话框
   * @param message 错误消息
   */
  async showErrorDialog(message: string): Promise<void> {
    try {
      await ElMessageBox.alert(
        message || 'Payment processing failed. Please try again.',
        'Payment Error',
        {
          confirmButtonText: 'OK',
          type: 'error',
          center: true,
        }
      );
    } catch (error) {
      // 用户取消或其他错误，使用通知作为降级
      this.showErrorNotification(message);
    }
  }

  /**
   * 显示错误通知
   * @param message 错误消息
   */
  showErrorNotification(message: string): void {
    try {
      ElNotification({
        title: 'Payment Error',
        message: message || 'Payment processing failed. Please try again.',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    } catch (error) {
      // 最后降级到浏览器原生 alert
      console.error('Failed to show error notification:', error);
      alert(`Payment Error: ${message}`);
    }
  }

  /**
   * 显示成功通知（轻量级）
   * @param message 成功消息
   */
  showSuccessNotification(message?: string): void {
    try {
      ElNotification({
        title: 'Payment Successful',
        message: message || 'Your subscription has been activated successfully.',
        type: 'success',
        duration: 4000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Failed to show success notification:', error);
    }
  }

  /**
   * 清理资源
   */
  private cleanup(): void {
    if (this.app) {
      this.app.unmount();
      this.app = null;
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
  }
}

// 导出单例实例
export const subscriptionDialogManager = new SubscriptionDialogManager();
